import {gql, useMutation, useQuery, NetworkStatus} from '@apollo/client';
import {AccountCharges, AccountChargesVariables} from '../../schema-types/AccountCharges';
import useAccountId from '../../hooks/useAccountId';
import {useReviewContext} from '../../contexts/ReviewContext';
import {UpdateCharge, UpdateChargeVariables} from '../../schema-types/UpdateCharge';
import {ResetCharge, ResetChargeVariables} from '../../schema-types/ResetCharge';
import validateUUID from '../../utils/validateUUID';

export const CHARGE_TYPE_FRAGMENT = gql`
  fragment ChargeTypeFragment on ChargeType {
    value
    isEditable
    tooltipText
    id
  }
`;

const CHARGE_FRAGMENT = gql`
  ${CHARGE_TYPE_FRAGMENT}
  fragment BeforeAfterChargeFragment on BeforeAfterChargeType {
    potCharges {
      id
      potName
      potId
      potValue
      productFeeCharge {
        ...ChargeTypeFragment
      }
      dfmFeeCharge {
        ...ChargeTypeFragment
      }
      fpFeeCharge {
        ...ChargeTypeFragment
      }
      sippFeeCharge {
        ...ChargeTypeFragment
      }
      modelPortfolioFeeCharge {
        ...ChargeTypeFragment
      }
      totalChargePercentage
      totalChargePound
    }
    summaryCharge {
      id
      potName
      potValue
      productFeeCharge
      dfmFeeCharge
      fpFeeCharge
      sippFeeCharge
      modelPortfolioFeeCharge
      totalChargePercentage
      totalChargePound
    }
  }
`;

export const GET_ACCOUNT_CHARGES = gql`
  ${CHARGE_FRAGMENT}
  query AccountCharges($accountId: UUID!, $reviewId: UUID!) {
    charges(accountId: $accountId, reviewId: $reviewId) {
      errors
      beforeCharge {
        ...BeforeAfterChargeFragment
      }
      afterCharge {
        ...BeforeAfterChargeFragment
      }
      initialCharge {
        totalInitialCharge
        charges {
          id
          name
          premiumAmount
          initialFeeChargePercentage
          initialFeeChargePound
        }
        summaryCharge {
          id
          name
          premiumAmount
          initialFeeChargePercentage
          initialFeeChargePound
        }
      }
    }
  }
`;

export const UPDATE_CHARGE = gql`
  mutation UpdateCharge($id: UUID!, $annualPercentageValue: Decimal!) {
    updateCharge(id: $id, annualPercentageValue: $annualPercentageValue) {
      ok
      errors
    }
  }
`;

export const RESET_CHARGE = gql`
  mutation ResetCharge($reviewId: UUID!) {
    resetCharge(reviewId: $reviewId) {
      ok
      errors
    }
  }
`;

export const usePlanFees = () => {
  const {accountId} = useAccountId();
  const {reviewId} = useReviewContext();

  const {
    data: accountChargesData,
    loading: gLoading,
    error: gError,
    networkStatus,
  } = useQuery<AccountCharges, AccountChargesVariables>(GET_ACCOUNT_CHARGES, {
    variables: {
      accountId: validateUUID(accountId)!,
      reviewId: validateUUID(reviewId)!,
    },
    skip: !validateUUID(accountId) || !reviewId,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  const [updateCharge, {data: uData, loading: uLoading, error: uError}] = useMutation<
    UpdateCharge,
    UpdateChargeVariables
  >(UPDATE_CHARGE, {
    refetchQueries: [GET_ACCOUNT_CHARGES],
  });

  const [resetCharge, {data: rData, loading: rLoading, error: rError}] = useMutation<
    ResetCharge,
    ResetChargeVariables
  >(RESET_CHARGE, {
    variables: {
      reviewId: validateUUID(reviewId)!,
    },
    refetchQueries: [GET_ACCOUNT_CHARGES],
  });

  return {
    beforeCharge: accountChargesData?.charges?.beforeCharge,
    updateCharge,
    resetCharge,
    loading: uLoading || rLoading || gLoading || networkStatus === NetworkStatus.refetch,
    error:
      uError?.message ||
      rError?.message ||
      gError?.message ||
      rData?.resetCharge?.errors?.[0] ||
      uData?.updateCharge?.errors?.[0] ||
      accountChargesData?.charges?.errors?.[0],
    initialCharge: accountChargesData?.charges?.initialCharge,
    postCharge: accountChargesData?.charges?.afterCharge,
  };
};
